import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Platform, View } from 'react-native';
import { DeviceTheme } from '../../../api/api.g';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hook';
import useTranslation from '../../../hooks/translation-hook';
import useUserPrefsService from '../../../hooks/userPrefsService-hook';
import NavigationElements from '../../../models/NavigationElements';
import { ThemePreference, userPrefsActions } from '../../../store/userPrefsSlice';
import CustomRadioButton from '../../common/CustomRadioButton';
import Loading from '../../common/Loading';
import { LabelType } from '../../common/Label';
import MyAccountPageWrapper from '../MyAccountPageWrapper';

const SettingsTheme = () => {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const userPrefsService = useUserPrefsService();

  const initialThemePreference: ThemePreference = useMemo(() => {
    if (userPrefs.themePreference !== undefined && userPrefs.themePreference !== null) {
      return userPrefs.themePreference;
    }

    return DeviceTheme.System;
  }, [userPrefs.themePreference]);

  const [selectedThemePreference, setSelectedThemePreference] = useState<ThemePreference>(initialThemePreference);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingThemePreference, setPendingThemePreference] = useState<ThemePreference | undefined>(undefined);
  const isWeb = Platform.OS === 'web';
  const loadingOverlayStyle = { backgroundColor: 'rgba(0, 0, 0, 0.55)' };

  useEffect(() => {
    setSelectedThemePreference(initialThemePreference);
  }, [initialThemePreference]);

  const isFrench = (userPrefs.uiLanguage ?? '').toLowerCase().startsWith('fr');

  const getThemeLabel = (type: 'system' | 'light' | 'dark') => {
    const key = `settings-theme-${type}`;
    const translated = t(key);
    if (translated !== key) {
      return translated;
    }

    if (type === 'system') {
      return isFrench ? 'Système' : 'System';
    }

    if (type === 'dark') {
      return isFrench ? 'Sombre' : 'Dark';
    }

    return isFrench ? 'Clair' : 'Light';
  };

  const getApplyingThemeLabel = () => {
    const key = 'settings-applying-theme';
    const translated = t(key);
    if (translated !== key) {
      return translated;
    }

    return isFrench ? 'Application du theme...' : 'Applying theme...';
  };

  const applyThemePreference = (nextThemePreference: ThemePreference) => {
    if (isLoading || nextThemePreference === selectedThemePreference) {
      return;
    }

    setIsLoading(true);
    setSelectedThemePreference(nextThemePreference);
    setPendingThemePreference(nextThemePreference);
  };

  useEffect(() => {
    if (!isLoading || pendingThemePreference === undefined) {
      return;
    }

    let cancelled = false;

    const applyThemeAsync = async () => {
      // Let the loading overlay render first before triggering theme-wide updates.
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (cancelled) {
        return;
      }

      dispatch(userPrefsActions.setThemePreference(pendingThemePreference));

      try {
        await userPrefsService.saveBasicInfo();
      } finally {
        if (!cancelled) {
          setPendingThemePreference(undefined);
          setIsLoading(false);
        }
      }
    };

    void applyThemeAsync();

    return () => {
      cancelled = true;
    };
  }, [isLoading, pendingThemePreference, dispatch, userPrefsService]);

  return (
    <>
      {isLoading && isWeb && (
        <Loading
          iconColor="#FFFFFF"
          textVariant={LabelType.titleWhiteSmall}
          text={getApplyingThemeLabel()}
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 2000, width: '100%', height: '100%', ...(loadingOverlayStyle as any) }}
        />
      )}
      {!isWeb && (
        <Modal visible={isLoading} transparent animationType="none">
          <Loading iconColor="#FFFFFF" textVariant={LabelType.titleWhiteSmall} text={getApplyingThemeLabel()} style={loadingOverlayStyle as any} />
        </Modal>
      )}
      <MyAccountPageWrapper
        navigationTitle={t('settings')}
        title={t('appearance')}
        navigationAction={NavigationElements.settings}
        titleStyle={{ textTransform: 'none' }}
      >
        <View>
          <View style={{ marginVertical: 8, marginTop: 16 }}>
            <CustomRadioButton
              textStyle={{ fontSize: 16, textTransform: 'capitalize' }}
              label={getThemeLabel('system')}
              value={DeviceTheme.System}
              checked={selectedThemePreference === DeviceTheme.System}
              onChange={(checked) => {
                if (checked) {
                  void applyThemePreference(DeviceTheme.System);
                }
              }}
            />
          </View>
          <View style={{ marginVertical: 8 }}>
            <CustomRadioButton
              textStyle={{ fontSize: 16, textTransform: 'capitalize' }}
              label={getThemeLabel('light')}
              value={DeviceTheme.Light}
              checked={selectedThemePreference === DeviceTheme.Light}
              onChange={(checked) => {
                if (checked) {
                  void applyThemePreference(DeviceTheme.Light);
                }
              }}
            />
          </View>
          <View style={{ marginVertical: 8 }}>
            <CustomRadioButton
              textStyle={{ fontSize: 16, textTransform: 'capitalize' }}
              label={getThemeLabel('dark')}
              value={DeviceTheme.Dark}
              checked={selectedThemePreference === DeviceTheme.Dark}
              onChange={(checked) => {
                if (checked) {
                  void applyThemePreference(DeviceTheme.Dark);
                }
              }}
            />
          </View>
        </View>
      </MyAccountPageWrapper>
    </>
  );
};

export default SettingsTheme;
